import React, { memo } from "react";
import { Box, Typography, Stack, Divider, Fade } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import FlexBetween from "components/FlexBetween";

const MotionBox = motion(Box);

const IndexBox = ({
    icon = <DragIndicator sx={{ color: "green.dark", fontSize: 24 }} />,
    title = "",
    index = "",
    description = "",
    props = [
        { label: "H", value: "", color: "info.main" },
        { label: "L", value: "", color: "error.main" },
    ],
}) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 100,
                overflow: "hidden",
                borderRadius: 1,
                border: 1,
                borderColor: "neutral.deep",
                backgroundColor: "slate.lightest",
            }}
        >
            {/* ================= HEADER ================= */}
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    px: 1,
                    py: 0.5,
                    backgroundColor: "primary.lightest",
                }}
            >
                <FlexBetween>{icon}</FlexBetween>

                {/* Animate title */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={title}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        transition={{ duration: 0.2 }}
                        style={{ flex: 1 }}
                    >
                        <Typography
                            variant="body1"
                            textAlign="center"
                            color="text.primary"
                        >
                            {title}
                        </Typography>
                    </motion.div>
                </AnimatePresence>
            </Box>

            <Divider />

            {/* ================= CONTENT ================= */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                }}
            >
                <Stack
                    width="100%"
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    {/* LEFT */}
                    <Stack flexGrow={1} alignItems="center" spacing={1}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1.1 }}
                                transition={{ duration: 0.25 }}
                            >
                                <Typography variant="h5">{index}</Typography>
                            </motion.div>
                        </AnimatePresence>

                        <Fade in timeout={300}>
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                        </Fade>
                    </Stack>

                    {/* RIGHT */}
                    <Stack minWidth={75} spacing={0.5}>
                        <AnimatePresence>
                            {props.map((item, i) => (
                                <MotionBox
                                    key={item.label} 
                                    layout
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 36,
                                                height: 24,
                                                backgroundColor: item.color,
                                            }}
                                        >
                                            {item.label}
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                flex: 1,
                                                textAlign: "center",
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Stack>
                                </MotionBox>
                            ))}
                        </AnimatePresence>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};

export default memo(IndexBox);